// Sending a question, outside the React tree.
//
// The obvious place for this is AiPanel, and that is where it started. It does
// not work there: the panel unmounts when it is closed and when "Take me
// there" navigates, so an answer still streaming would be aborted halfway by
// the very action the customer took in response to it. Owning the request here
// means the conversation is driven by the store, and the panel is free to come
// and go while an answer arrives.
//
// Dispatching to the store from a plain module follows what axiosApi.ts
// already does for auth errors.

import { store } from "store/store";
import {
  appendAiToken,
  patchAiMessage,
  setAiBusy,
  startAiTurn,
} from "store/slices/aiSlice";
import { askAi, currentPage, toHistory } from "./aiApi";

let inFlight: AbortController | null = null;

/** Stop any answer currently streaming. Used by "New chat" and by logout. */
export function abortAiQuestion() {
  inFlight?.abort();
  inFlight = null;
  if (store.getState().ai.busy) store.dispatch(setAiBusy(false));
}

/**
 * Ask one question and stream the answer into the store.
 *
 * Returns once the answer is complete. Callers do not need to await it — the
 * UI follows the store — but it is awaitable for tests.
 */
export async function sendAiQuestion(question: string): Promise<void> {
  const trimmed = question.trim();
  if (!trimmed || store.getState().ai.busy) return;

  // Captured before the turn is pushed, so the question being asked is not
  // also in its own history.
  const history = toHistory(store.getState().ai.messages);

  store.dispatch(startAiTurn(trimmed));

  // startAiTurn pushes the question and then the empty answer it streams into,
  // so the answer is the last message. Reading it back from the store rather
  // than minting an id here keeps the counter in one place.
  const { messages } = store.getState().ai;
  const answerId = messages[messages.length - 1].id;

  const controller = new AbortController();
  inFlight = controller;

  try {
    await askAi(
      trimmed,
      history,
      {
        onToken: (text) => store.dispatch(appendAiToken({ id: answerId, text })),
        onAction: (action) => store.dispatch(patchAiMessage({ id: answerId, action })),
      },
      controller.signal,
      currentPage()
    );
  } catch (error: any) {
    // An abort is a deliberate cancellation — "New chat" or logout — and the
    // message it was writing into is already gone.
    if (controller.signal.aborted) return;

    // Shown in the transcript next to the question it belongs to, rather than
    // as a toast that leaves an empty bubble behind.
    store.dispatch(
      patchAiMessage({
        id: answerId,
        content: error?.message || "Sorry — something went wrong. Please try again.",
        failed: true,
      })
    );
  } finally {
    if (inFlight === controller) {
      inFlight = null;
      store.dispatch(setAiBusy(false));
    }
  }
}
